import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { Box, Button, Column } from '@tempmee/react';
import { Icon } from '~/commonComponents';
import { FormActions } from '~/components/shared/forms';
import { SectionCard, ShiftRequestHeader } from '~/components/ShiftRequestRedesign/common';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { useNavigationDetector } from '~/hooks/useNavigationDetector';
import { fireShiftRequestFillPreferencesSubmitted } from '~/posthogs/events';
import { useIsFormValid } from '../hooks/useIsFormValid';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { SpeedUpCoverageSection } from './SpeedUpCoverageSection';

export const AddInvitesScreen = () => {
  const { control, watch } = useFormContext<IShiftDetailsFormValues>();
  const navigate = useNavigate();
  const isFormValid = useIsFormValid();
  const dates = watch('dates');

  const [showNavigationGuard, setShowNavigationGuard] = useState(false);

  const onNavigationAttempt = useCallback(() => {
    setShowNavigationGuard(true);
  }, []);

  const { allowNavigation, navigateToPending, clearPendingNavigation } = useNavigationDetector({
    when: true,
    onNavigationAttempt,
  });

  useEffect(() => {
    if (dates.length === 0) {
      allowNavigation();
      navigate('/dashboard/shift-request/select-dates', { replace: true });
      return;
    }
    if (!isFormValid) {
      allowNavigation();
      navigate('/dashboard/multi-day/info', { replace: true });
    }
  }, [dates.length, isFormValid, allowNavigation, navigate]);

  const handleBack = useCallback(() => {
    allowNavigation();
    navigate('/dashboard/multi-day/info');
  }, [allowNavigation, navigate]);

  const handleReviewShifts = useCallback(() => {
    fireShiftRequestFillPreferencesSubmitted();
    allowNavigation();
    navigate('/dashboard/multi-day/confirm');
  }, [allowNavigation, navigate]);

  return (
    <ShiftRequestHeader currentStep={2} onBack={handleBack} maxWidth={1120}>
      <Column align="center" style={{ gap: 24, width: 730, alignSelf: 'center' }}>
        <SectionCard title="Speed Up Your Coverage" testId="speed-up-card">
          <SpeedUpCoverageSection control={control} watch={watch} />
        </SectionCard>
      </Column>

      <FormActions>
        <Button
          data-testid="review-shifts-button"
          data-cy="review-shifts-button"
          onClick={handleReviewShifts}
          size="lg"
        >
          Review Shifts
        </Button>
      </FormActions>

      {showNavigationGuard && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => (
            <Box style={{ marginBottom: 20 }}>
              <Icon name="warning-outline" size={80} />
            </Box>
          )}
          title="Are You Sure You Want to Leave?"
          description="Your shifts haven't been posted yet. Leaving now means the progress made to your request won't be saved."
          leftButtonText="Go Back"
          leftButtonAction={() => {
            clearPendingNavigation();
            setShowNavigationGuard(false);
          }}
          rightButtonText="Leave Anyway"
          rightButtonAction={navigateToPending}
          closePopup={() => {
            clearPendingNavigation();
            setShowNavigationGuard(false);
          }}
          dataTestId="navigation-guard-popup"
        />
      )}
    </ShiftRequestHeader>
  );
};
